import { useState } from "react";
import { Heart, Star, X } from "lucide-react";
import { toast } from "sonner";
import type { DiscoveryCandidate, LikeAction, RegisterActionResult } from "@/lib/discovery/types";
import CandidateCard from "./CandidateCard";

interface Props {
  candidates: DiscoveryCandidate[];
  onAction: (
    targetId: string,
    action: LikeAction,
  ) => Promise<RegisterActionResult | { error: string }>;
  onEmpty?: () => void;
}

const SwipeStack = ({ candidates, onAction, onEmpty }: Props) => {
  const [pending, setPending] = useState(false);

  const top = candidates[0];
  const next = candidates[1];

  if (!top) {
    return (
      <div className="rounded-3xl border border-dashed border-border p-12 text-center">
        <p className="text-base font-medium">You're all caught up.</p>
        <p className="mt-2 text-sm text-muted-foreground">
          New members appear here as they join. Check back soon.
        </p>
        {onEmpty && (
          <button
            type="button"
            onClick={onEmpty}
            className="mt-6 rounded-full border border-border px-5 py-2 text-sm hover:bg-accent transition-colors"
          >
            Refresh
          </button>
        )}
      </div>
    );
  }

  const handle = async (action: LikeAction) => {
    if (pending) return;
    setPending(true);
    const res = await onAction(top.id, action);
    setPending(false);
    if ("error" in res) {
      toast.error(res.error);
      return;
    }
    if (res.limited) {
      toast.warning(
        `Daily limit reached (${res.used}/${res.cap}). Upgrade for more.`,
      );
      return;
    }
    if (res.matched) {
      toast.success(
        action === "super_signal" ? "Super Signal — it's a match!" : "It's a match!",
      );
    }
  };

  return (
    <div className="relative">
      {/* stacked next card */}
      {next && (
        <div className="pointer-events-none absolute inset-0 -z-10 scale-95 opacity-60 blur-[1px]">
          <CandidateCard candidate={next} />
        </div>
      )}

      <CandidateCard candidate={top} />

      <div className="mt-6 flex items-center justify-center gap-4">
        <button
          type="button"
          aria-label="Pass"
          disabled={pending}
          onClick={() => handle("pass")}
          className="grid h-14 w-14 place-items-center rounded-full border border-border bg-card shadow-card hover:bg-accent transition disabled:opacity-50"
        >
          <X className="h-6 w-6 text-muted-foreground" />
        </button>
        <button
          type="button"
          aria-label="Super Signal"
          disabled={pending}
          onClick={() => handle("super_signal")}
          className="grid h-12 w-12 place-items-center rounded-full bg-primary text-primary-foreground shadow-elevated hover:opacity-90 transition disabled:opacity-50"
        >
          <Star className="h-5 w-5" />
        </button>
        <button
          type="button"
          aria-label="Like"
          disabled={pending}
          onClick={() => handle("like")}
          className="grid h-14 w-14 place-items-center rounded-full bg-foreground text-background shadow-elevated hover:opacity-90 transition disabled:opacity-50"
        >
          <Heart className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
};

export default SwipeStack;
